import { useEffect, useState } from "react";
import { useMotionValue, useSpring } from "framer-motion";
import { TrendingDown, PiggyBank } from "lucide-react";
import { useLang } from "../context/LangContext";
import { waLink } from "../constants";
import { Reveal } from "./Reveal";

const AnimatedNumber = ({ value, prefix = "USD ", testid }) => {
  const mv = useMotionValue(value);
  const spring = useSpring(mv, { stiffness: 70, damping: 22 });
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    mv.set(value);
  }, [value, mv]);

  useEffect(() => spring.on("change", (v) => setDisplay(Math.round(v))), [spring]);

  return (
    <span data-testid={testid}>
      {prefix}
      {display.toLocaleString("en-US")}
    </span>
  );
};

export const Savings = () => {
  const { t } = useLang();
  const c = t.savings;
  const [spend, setSpend] = useState(300000);

  const stdFee = (spend * 1.8) / 100;
  const promoFee = (spend * 0.9) / 100;
  const monthlySave = stdFee - promoFee;
  const annualSave = monthlySave * 12;

  return (
    <section id="savings" className="relative py-20 lg:py-28">
      <div className="pointer-events-none absolute right-[-100px] top-1/3 h-[380px] w-[380px] rounded-full bg-violet-700/10 blur-[130px]" />
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="mx-auto max-w-2xl text-center">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-blue-400">{c.tag}</span>
          <h2 className="mt-4 font-display text-3xl font-black leading-tight text-white sm:text-4xl lg:text-5xl">
            {c.titleA}
            <br />
            <span className="text-gradient">{c.titleB}</span>
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-slate-400 sm:text-base">{c.subtitle}</p>
        </Reveal>

        <Reveal delay={0.1} className="mt-14">
          <div className="glass mx-auto max-w-5xl rounded-[28px] p-8 sm:p-10 lg:p-12">
            {/* spend control */}
            <div className="text-center">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{c.monthlySpend}</p>
              <p data-testid="calculator-spend-display" className="mt-3 font-display text-4xl font-black text-white sm:text-5xl">
                USD {spend.toLocaleString("en-US")}
              </p>
              <input
                type="range"
                min="50000"
                max="1000000"
                step="10000"
                value={spend}
                onChange={(e) => setSpend(Number(e.target.value))}
                data-testid="calculator-slider"
                className="mx-auto mt-7 max-w-xl"
                aria-label={c.monthlySpend}
              />
              <div className="mx-auto mt-2 flex max-w-xl justify-between text-[10px] font-semibold text-slate-600">
                <span>USD 50K</span>
                <span>USD 1M</span>
              </div>
            </div>

            {/* fees comparison */}
            <div className="mt-10 grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6 text-center">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
                  {c.standardTitle} · 1.8%
                </p>
                <p className="mt-4 font-display text-3xl font-black text-slate-400 sm:text-4xl">
                  <AnimatedNumber value={stdFee} testid="standard-fee-display" />
                </p>
                <p className="mt-2 text-xs text-slate-600">{c.estFee}</p>
              </div>
              <div className="glow-card rounded-2xl bg-gradient-to-b from-blue-950/60 to-navy-950 p-6 text-center">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-300">
                  {c.promoTitle} · 0.9%
                </p>
                <p className="mt-4 font-display text-3xl font-black text-white sm:text-4xl">
                  <AnimatedNumber value={promoFee} testid="promo-fee-display" />
                </p>
                <p className="mt-2 text-xs text-slate-500">{c.estFee}</p>
              </div>
            </div>

            {/* savings */}
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/[0.06] p-6 text-center">
                <p className="flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-emerald-300">
                  <TrendingDown className="h-4 w-4" />
                  {c.monthlySave}
                </p>
                <p className="mt-4 font-display text-3xl font-black text-emerald-300 sm:text-4xl">
                  <AnimatedNumber value={monthlySave} testid="monthly-savings-display" />
                </p>
              </div>
              <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/[0.06] p-6 text-center">
                <p className="flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-emerald-300">
                  <PiggyBank className="h-4 w-4" />
                  {c.annualSave}
                </p>
                <p className="mt-4 font-display text-3xl font-black text-emerald-300 sm:text-4xl">
                  <AnimatedNumber value={annualSave} testid="annual-savings-display" />
                </p>
              </div>
            </div>

            {/* save up to banner */}
            <div className="mt-10 text-center">
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-slate-400">{c.saveUpTo}</p>
              <p data-testid="save-up-to-display" className="text-gradient mt-2 font-display text-4xl font-black sm:text-6xl">
                USD {annualSave.toLocaleString("en-US")} / Y
              </p>
              <a
                href={waLink(t.contact.msgDefault)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="claim-savings-btn"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 px-8 py-3.5 text-sm font-bold text-white shadow-[0_0_32px_rgba(59,130,246,0.4)] transition-transform duration-300 hover:scale-[1.04]"
              >
                {t.nav.cta}
              </a>
            </div>

            <p className="mt-8 text-center text-[11px] leading-relaxed text-slate-600">{c.footnote}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
